import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiArrowRight,
  FiBriefcase,
  FiCheck,
  FiChevronDown,
  FiClock,
  FiCreditCard,
  FiGlobe,
  FiHeadphones,
  FiMenu,
  FiNavigation,
  FiShield,
  FiStar,
  FiTrendingDown,
  FiX,
  FiZap,
} from "react-icons/fi";
import logoZuno from "../assets/zuno-logo.png";
import heroCar from "../assets/zuno/zuno-landing-hero-white-car.png";
import { LEGAL_IDENTITY, publicOperatorDescription } from "../config/legalIdentity";
import "./LandingPage.css";

const navItems = [
  ["Para passageiros", "#passageiros"],
  ["Para motoristas", "#motoristas"],
  ["Empresas", "#sobre"],
  ["Sobre a Zuno", "#sobre"],
];

const benefits = [
  {
    icon: <FiZap />,
    title: "Rapidez real",
    text: "Encontre seu motorista em poucos minutos e acompanhe cada etapa da viagem.",
  },
  {
    icon: <FiShield />,
    title: "Viagem segura",
    text: "Proteção em cada etapa da corrida para você viajar com tranquilidade.",
  },
  {
    icon: <FiTrendingDown />,
    title: "Taxas justas",
    text: "Planos pensados para valorizar o motorista e manter preços justos.",
  },
  {
    icon: <FiHeadphones />,
    title: "Suporte humano",
    text: "Atendimento próximo e ágil sempre que você precisar.",
  },
];

const purposeItems = [
  ["Operação 24h", "Suporte e monitoramento disponíveis durante toda a operação.", <FiClock />],
  ["Avaliação transparente", "Notas reais e critérios claros para todos os lados.", <FiStar />],
  ["Pagamento instantâneo", "Acompanhe corridas e ganhos com clareza em tempo real.", <FiCreditCard />],
  ["SOS dentro da corrida", "Ajuda acessível quando precisar, diretamente no app.", <FiHeadphones />],
];

function useReveal() {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (!window.IntersectionObserver) {
      node.classList.add("is-visible");
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        node.classList.add("is-visible");
        observer.disconnect();
      }
    }, { threshold: 0.12, rootMargin: "0px 0px -8%" });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return ref;
}

function Reveal({ as: Tag = "section", className = "", children, ...props }) {
  const ref = useReveal();
  return <Tag ref={ref} className={`zuno-landing-reveal ${className}`} {...props}>{children}</Tag>;
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 18);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  return (
    <main className="zuno-landing" id="topo">
      <Header menuOpen={menuOpen} scrolled={scrolled} onMenu={() => setMenuOpen((value) => !value)} onNavigate={go} />
      <Hero onNavigate={go} />
      <Benefits />
      <DriverPlans onNavigate={go} />
      <Purpose />
      <Footer />
    </main>
  );
}

function Header({ menuOpen, scrolled, onMenu, onNavigate }) {
  return (
    <header className={`zuno-landing-header ${scrolled ? "is-scrolled" : ""}`}>
      <a className="zuno-landing-logo" href="#topo">
        <img src={logoZuno} alt="Zuno" />
      </a>

      <nav className={`zuno-landing-nav ${menuOpen ? "is-open" : ""}`} aria-label="Navegação principal">
        {navItems.map(([label, href], index) => (
          <a key={label} href={href}>
            {label}
            {index === 0 && <FiChevronDown aria-hidden="true" />}
          </a>
        ))}
      </nav>

      <div className="zuno-landing-actions">
        <button type="button" className="zuno-locale" aria-label="Idioma">
          <FiGlobe /> PT-BR <FiChevronDown />
        </button>
        <button type="button" className="zuno-btn zuno-btn-ghost" onClick={() => onNavigate("/login")}>Entrar</button>
        <button type="button" className="zuno-btn zuno-btn-primary" onClick={() => onNavigate("/login")}>Criar conta</button>
        <button type="button" className="zuno-menu-button" aria-label={menuOpen ? "Fechar menu" : "Abrir menu"} aria-expanded={menuOpen} onClick={onMenu}>
          {menuOpen ? <FiX /> : <FiMenu />}
        </button>
      </div>
    </header>
  );
}

function Hero({ onNavigate }) {
  return (
    <section className="zuno-hero-white">
      <div className="zuno-hero-copy">
        <span className="zuno-eyebrow">Zuno Mobilidade</span>
        <h1>Mobilidade <span>mais justa</span> para a cidade.</h1>
        <p>Uma plataforma feita para valorizar motoristas, simplificar a vida de passageiros e entregar corridas mais rápidas, seguras e transparentes.</p>
        <div className="zuno-hero-buttons">
          <button type="button" className="zuno-btn zuno-btn-primary zuno-btn-large" onClick={() => onNavigate("/login")}>
            Solicitar corrida <FiArrowRight />
          </button>
          <button type="button" className="zuno-btn zuno-btn-outline zuno-btn-large" onClick={() => onNavigate("/cadastro-motorista")}>
            Dirigir com a Zuno
          </button>
        </div>
      </div>

      <div className="zuno-hero-visual" aria-label="Carro Zuno em cidade conectada">
        <img src={heroCar} alt="Carro branco Zuno em uma cidade moderna" />
        <span className="zuno-motion-line line-one" />
        <span className="zuno-motion-line line-two" />
        <span className="zuno-motion-line line-three" />
      </div>
    </section>
  );
}

function Benefits() {
  return (
    <Reveal className="zuno-benefits" id="passageiros">
      {benefits.map((benefit) => (
        <article className="zuno-benefit-card" key={benefit.title}>
          <i>{benefit.icon}</i>
          <h2>{benefit.title}</h2>
          <p>{benefit.text}</p>
        </article>
      ))}
    </Reveal>
  );
}

function DriverPlans({ onNavigate }) {
  return (
    <Reveal className="zuno-plans" id="motoristas">
      <div className="zuno-section-head zuno-plans-head">
        <div>
          <span className="zuno-eyebrow">Planos para motoristas</span>
          <h2>Mais dinheiro <span>no bolso</span>, mais controle na sua rotina.</h2>
        </div>
        <p>Escolha o plano que faz mais sentido para você e fique com mais do que realmente importa.</p>
      </div>

      <div className="zuno-plan-grid">
        <PlanCard
          label="Plano Mensal"
          value="100%"
          text="Para quem roda bastante: uma mensalidade fixa e 100% do valor das corridas fica com o motorista."
          pill="Feito para valorizar o motorista"
          onClick={() => onNavigate("/plano-mensalidade")}
        />
        <PlanCard
          label="Plano Flex"
          value="8%"
          text="Para começar leve: sem mensalidade e com apenas 8% de taxa por corrida."
          pill="Mais flexibilidade para a sua rotina"
          tone="pink"
          onClick={() => onNavigate("/plano-flex")}
        />
      </div>
    </Reveal>
  );
}

function PlanCard({ label, value, text, pill, tone = "blue", onClick }) {
  return (
    <article className={`zuno-plan-card tone-${tone}`}>
      <small>{label}</small>
      <strong>{value}</strong>
      <p>{text}</p>
      <span><FiCheck /> {pill}</span>
      <button type="button" onClick={onClick}>Conhecer o plano <FiArrowRight /></button>
    </article>
  );
}

function Purpose() {
  return (
    <Reveal className="zuno-purpose" id="sobre">
      <div className="zuno-purpose-mark" aria-hidden="true">
        <span />
        <span />
        <span />
        <img src={logoZuno} alt="" />
      </div>
      <div className="zuno-purpose-copy">
        <span className="zuno-eyebrow">Por que a Zuno nasceu</span>
        <h2>Uma plataforma criada para <span>equilibrar o jogo</span> da mobilidade.</h2>
        <p>A Zuno nasceu olhando para quem sustenta a mobilidade todos os dias: o motorista.</p>
        <p>A ideia é simples e forte: tecnologia deve gerar renda mais justa, mais controle e uma experiência melhor para todos os lados da corrida.</p>
        <p>Para o passageiro, isso vira rapidez, segurança e atendimento profissional. Para o motorista, previsibilidade, autonomia e taxas mais leves.</p>
      </div>
      <div className="zuno-purpose-list">
        {purposeItems.map(([title, text, icon]) => (
          <article key={title}>
            <i>{icon}</i>
            <div>
              <h3>{title}</h3>
              <p>{text}</p>
            </div>
          </article>
        ))}
      </div>
    </Reveal>
  );
}

/*
function Presence() {
  return (
    <Reveal className="zuno-presence" id="empresas">
      <div className="zuno-presence-copy">
        <span className="zuno-eyebrow">Conectando mobilidade</span>
        <h2>Presença em <span>4 continentes.</span></h2>
        <p>Conectamos pessoas e cidades para transformar cada trajeto em uma experiência melhor.</p>
        <a href="#sobre">Saiba mais</a>
      </div>
      <WorldMap />
    </Reveal>
  );
}

function WorldMap() {
  const inside = (x, y, cx, cy, rx, ry) => (((x - cx) / rx) ** 2) + (((y - cy) / ry) ** 2) <= 1;
  const mapDots = [];

  for (let y = 58; y <= 238; y += 10) {
    for (let x = 58; x <= 704; x += 10) {
      const onLand =
        inside(x, y, 155, 118, 82, 45) ||
        inside(x, y, 116, 158, 52, 36) ||
        inside(x, y, 212, 154, 48, 38) ||
        inside(x, y, 240, 219, 36, 62) ||
        inside(x, y, 356, 124, 62, 33) ||
        inside(x, y, 402, 166, 83, 54) ||
        inside(x, y, 350, 217, 50, 57) ||
        inside(x, y, 504, 126, 124, 48) ||
        inside(x, y, 570, 170, 93, 55) ||
        inside(x, y, 632, 210, 45, 26);
      const cutout =
        inside(x, y, 278, 93, 42, 28) ||
        inside(x, y, 500, 232, 62, 38) ||
        inside(x, y, 95, 214, 54, 42);
      const keep = ((x * 11 + y * 17) % 7) !== 0;

      if (onLand && !cutout && keep) {
        mapDots.push([x, y, ((x + y) % 30 === 0) ? 2.2 : 1.55]);
      }
    }
  }

  return (
    <svg className="zuno-world-map" viewBox="46 72 668 170" role="img" aria-label="Mapa mundial com conexões da Zuno">
      <defs>
        <radialGradient id="zuno-map-dot" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="48%" stopColor="#58b5ff" />
          <stop offset="100%" stopColor="#8d5cff" stopOpacity=".15" />
        </radialGradient>
        <linearGradient id="zuno-map-line" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#22d7f0" />
          <stop offset="52%" stopColor="#5b8cff" />
          <stop offset="100%" stopColor="#b54cff" />
        </linearGradient>
      </defs>
      <g className="map-grid" aria-hidden="true">
        {Array.from({ length: 14 }, (_, index) => (
          <path key={`lat-${index}`} d={`M58 ${54 + index * 14}C214 ${42 + index * 6} 530 ${42 + index * 6} 704 ${54 + index * 14}`} />
        ))}
      </g>
      <g className="map-dot-field">
        {mapDots.map(([cx, cy, r]) => (
          <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={r} />
        ))}
      </g>
      <path className="map-route route-one" d="M156 158C232 80 348 73 443 134s156 55 225-26" />
      <path className="map-route route-two" d="M204 202c77-61 155-75 234-41 78 34 139 31 183-9" />
      <path className="map-route route-three" d="M278 116c86 55 174 62 264 18" />
      {[[156, 158], [278, 116], [443, 134], [668, 108], [204, 202], [542, 134], [621, 152]].map(([cx, cy]) => (
        <circle key={`${cx}-${cy}`} className="map-node" cx={cx} cy={cy} r="8" />
      ))}
    </svg>
  );
}

function FinalCTA({ onNavigate }) {
  return (
    <Reveal className="zuno-final-cta">
      <div>
        <h2>Faça parte da nova mobilidade.</h2>
        <p>Cadastre-se e tenha uma experiência única com a Zuno.</p>
      </div>
      <div className="zuno-final-actions">
        <button type="button" className="zuno-btn zuno-btn-primary zuno-btn-large" onClick={() => onNavigate("/login")}>
          Criar conta grátis <FiArrowRight />
        </button>
        <button type="button" className="zuno-btn zuno-btn-dark-outline zuno-btn-large" onClick={() => onNavigate("/cadastro-motorista")}>
          Quero dirigir
        </button>
      </div>
      <img src={logoZuno} alt="" aria-hidden="true" />
    </Reveal>
  );
}

*/
function Footer() {
  return (
    <footer className="zuno-footer">
      <div className="zuno-footer-brand">
        <img src={logoZuno} alt="Zuno" />
        <p>Mobilidade mais justa para a cidade.</p>
        <small>{publicOperatorDescription()}</small>
        <small>© 2026 Zuno. Todos os direitos reservados.</small>
      </div>
      <FooterColumn title="Institucional" links={["Sobre a Zuno", "Para motoristas", "Empresas", "Contato"]} />
      <FooterColumn title="Legal" links={["Termos de uso", "Privacidade", "Política de cookies"]} />
      <div className="zuno-footer-social">
        <strong>Siga a Zuno</strong>
        <span><FiGlobe /><FiBriefcase /><FiNavigation /></span>
      </div>
    </footer>
  );
}

function FooterColumn({ title, links }) {
  const hrefFor = (label) => {
    if (label === "Termos de uso") return "/termos";
    if (label === "Privacidade") return "/privacidade";
    if (label === "Política de cookies") return "/cookies";
    if (label === "Para motoristas") return "#motoristas";
    if (label === "Empresas") return "#sobre";
    if (label === "Contato") return `mailto:${LEGAL_IDENTITY.supportEmail}`;
    return "#sobre";
  };

  return (
    <div className="zuno-footer-column">
      <strong>{title}</strong>
      {links.map((label) => <a key={label} href={hrefFor(label)}>{label}</a>)}
    </div>
  );
}
